import type { FocusEvent, KeyboardEvent } from 'react';

import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

import type { SmartDateTimeFieldPopoverRenderProps } from './SmartDateTimeField';

/**
 * A `render.popover` for the smart date and time field. The calendar opens in a popup inside the field rather than a
 * portal, so a dialog around the field keeps the focus and the clicks in it. On touch screens the native picker lies
 * over the trigger instead.
 */
export const CalendarPopover = ({
  open,
  onOpenChange,
  trigger,
  content,
  showNativePicker,
  nativePickerProps,
}: SmartDateTimeFieldPopoverRenderProps) => {
  const floating = useFloating({
    open,
    onOpenChange,
    // Beside the field when there is room, so the popup stays off the footer below the field.
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ['left-start', 'bottom-end', 'top-end'] }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const interactions = useInteractions([
    useClick(floating.context),
    // Closing on the click, after the press moved the focus, lets the time field confirm the typed time on blur
    // before the popup unmounts it.
    useDismiss(floating.context, { escapeKey: false, outsidePressEvent: 'click' }),
  ]);

  if (showNativePicker) {
    return (
      <span className="relative inline-flex">
        <span inert className="inline-flex">
          {trigger}
        </span>
        <input
          {...nativePickerProps}
          className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
      </span>
    );
  }

  // Focus that leaves both the popup and its trigger closes the popup, like a click outside. The time field confirms
  // its time on its own blur first.
  const handleFocusOut = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    const staysOnPicker =
      next instanceof Node &&
      [floating.refs.floating.current, floating.refs.domReference.current].some((element) => element?.contains(next));
    if (!staysOnPicker) onOpenChange(false);
  };

  // Escape closes the popup and stops there, so a dialog around the field stays open.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!open || event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    onOpenChange(false);
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: catches Escape from the trigger and the popup inside it
    <span className="inline-flex" onKeyDown={handleKeyDown}>
      <span
        ref={floating.refs.setReference}
        className="inline-flex"
        {...interactions.getReferenceProps({ onBlur: handleFocusOut })}
      >
        {trigger}
      </span>
      {open ? (
        <div
          ref={floating.refs.setFloating}
          style={floating.floatingStyles}
          className="z-[100]"
          {...interactions.getFloatingProps({ onBlur: handleFocusOut })}
        >
          {content}
        </div>
      ) : null}
    </span>
  );
};
